/**
 * Extrai o ID do vídeo do YouTube de qualquer formato de URL
 * Suporta todos os formatos: watch, youtu.be, embed, shorts, mobile, etc.
 * 
 * @param {string} url - URL do YouTube em qualquer formato
 * @returns {string|null} - ID do vídeo (11 caracteres) ou null se não encontrado
 */
export function extractYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Regex robusta que cobre todos os formatos de URL do YouTube
  const regex = /(?:youtube\.com\/(?:.*v=|v\/|embed\/|shorts\/)|youtu\.be\/|youtube-nocookie\.com\/embed\/|m\.youtube\.com\/watch\?v=)([A-Za-z0-9_-]{11})/;
  
  const match = url.match(regex);
  return match ? match[1] : null;
}

/**
 * Gera a URL da thumbnail do YouTube em alta resolução
 * 
 * @param {string} videoId - ID do vídeo do YouTube
 * @returns {string} - URL da thumbnail em maxresdefault
 */
export function getYouTubeThumbnailUrl(videoId) {
  if (!videoId) {
    return '';
  }
  
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

/**
 * Gera a URL da thumbnail do YouTube com fallback para diferentes qualidades
 * 
 * @param {string} videoId - ID do vídeo do YouTube
 * @param {string} quality - Qualidade da thumbnail (maxresdefault, hqdefault, mqdefault, default)
 * @returns {string} - URL da thumbnail
 */
export function getYouTubeThumbnailUrlWithQuality(videoId, quality = 'maxresdefault') {
  if (!videoId) {
    return '';
  }
  
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * Função completa que extrai o ID e gera a thumbnail URL
 * 
 * @param {string} url - URL do YouTube em qualquer formato
 * @param {string} quality - Qualidade da thumbnail (opcional)
 * @returns {string} - URL da thumbnail ou string vazia se não conseguir extrair o ID
 */
export function getYouTubeThumbnailFromUrl(url, quality = 'maxresdefault') {
  const videoId = extractYouTubeVideoId(url);
  return getYouTubeThumbnailUrlWithQuality(videoId, quality);
}

/**
 * Função que tenta diferentes qualidades de thumbnail como fallback
 * 
 * @param {string} url - URL do YouTube em qualquer formato
 * @returns {string} - URL da thumbnail com fallback automático
 */
export function getYouTubeThumbnailWithFallback(url) {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {
    return '';
  }
  
  // Tenta maxresdefault primeiro, depois hqdefault como fallback
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

/**
 * Valida se uma string é um ID válido do YouTube
 * 
 * @param {string} videoId - ID do vídeo para validar
 * @returns {boolean} - true se for um ID válido
 */
export function isValidYouTubeVideoId(videoId) {
  if (!videoId || typeof videoId !== 'string') {
    return false;
  }
  
  // ID do YouTube sempre tem 11 caracteres alfanuméricos, hífens e underscores
  return /^[A-Za-z0-9_-]{11}$/.test(videoId);
}

/**
 * Normaliza uma URL do YouTube para o formato padrão watch
 * 
 * @param {string} url - URL do YouTube em qualquer formato
 * @returns {string|null} - URL normalizada ou null se não for uma URL válida
 */
export function normalizeYouTubeUrl(url) {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {
    return null;
  }
  
  return `https://www.youtube.com/watch?v=${videoId}`;
}

// Exemplos de URLs que a função suporta:
/*
✅ https://youtu.be/VIDEO_ID
✅ https://youtu.be/VIDEO_ID?t=120
✅ https://www.youtube.com/watch?v=VIDEO_ID
✅ https://youtube.com/watch?v=VIDEO_ID&t=120s
✅ https://www.youtube.com/watch?v=VIDEO_ID&list=PLxxxx
✅ http://youtube.com/watch?v=VIDEO_ID
✅ https://www.youtube.com/embed/VIDEO_ID
✅ https://www.youtube.com/embed/VIDEO_ID?start=30
✅ https://www.youtube-nocookie.com/embed/VIDEO_ID
✅ https://m.youtube.com/watch?v=VIDEO_ID
✅ https://www.youtube.com/watch?v=VIDEO_ID&feature=share
✅ https://youtu.be/VIDEO_ID?si=abc123xyz
✅ https://www.youtube.com/shorts/VIDEO_ID
*/
